import { useCallback, useContext, useEffect, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';

import HandleWidgetModal from '../components/custom-tab/handle-widget-modal';
import RenderWidget from '../components/custom-tab/render-widget';
import { getStorage, setStorage } from '../utils/storage_service';

import useLayout, { KEYS } from './layout-hook';
import { CustomWidget, WidgetKeys } from './model/custom-widget';
import usePersistentState from './use-persistent-state';
import { WebSocketContext } from './websocket-context/websocket-provider';

const ResponsiveGridLayout = WidthProvider(Responsive);

const DEFAULT_CLEAR_LAYOUT = {
  [KEYS.LG]: [],
  [KEYS.MD]: [],
  [KEYS.SM]: [],
  [KEYS.XS]: [],
  [KEYS.XXS]: [],
};

const useWidgets = (
  key: string,
  defaultWidgets?: CustomWidget[],
  defaultLayout?: any,
  isCustomTab?: boolean,
  keepDefaultWidgets?: boolean,
  keepWidgets?: CustomWidget[],
) => {
  const { unsubscribeByWidget: unsubscribe } = useContext(WebSocketContext);

  const { setLayouts, layouts, maximized, maximizeCard } = useLayout(
    key,
    defaultLayout || DEFAULT_CLEAR_LAYOUT,
  );
  const [widgets, setWidgets] = usePersistentState<CustomWidget[]>(
    ['WIDGETS', key],
    'widgets',
    defaultWidgets || [],
  );

  const [openCreateWidgetModal, setOpenCreateWidgetModal] = useState(false);
  const [currentWidget, setCurrentWidget] = useState<string | undefined>();
  const [isDeleteWidget, setIsDeleteWidget] = useState(false);

  const handleCloseCreateWidgetModal = useCallback(() => {
    setOpenCreateWidgetModal(false);
  }, []);

  const handleOpenModal = () => {
    setOpenCreateWidgetModal(true);
  };

  const handleResetLayout = () => {
    let newLayouts: any = DEFAULT_CLEAR_LAYOUT;

    widgets.forEach((widget, index) => {
      const widgetForLayout12Columns = {
        i: widget.id,
        x: 6 * (index % 2),
        y: 4 * Math.floor(index / 2),
        w: 6,
        h: 4,
      };

      const widgetForLayoutOneColumn = {
        i: widget.id,
        x: 0,
        y: 6 * index,
        w: 12,
        h: 4,
      };

      newLayouts = {
        [KEYS.LG]: [...newLayouts[KEYS.LG], widgetForLayout12Columns],
        [KEYS.MD]: [...newLayouts[KEYS.MD], widgetForLayout12Columns],
        [KEYS.SM]: [...newLayouts[KEYS.SM], widgetForLayout12Columns],
        [KEYS.XS]: [...newLayouts[KEYS.XS], widgetForLayoutOneColumn],
        [KEYS.XXS]: [...newLayouts[KEYS.XXS], widgetForLayoutOneColumn],
      };
    });

    setLayouts(newLayouts);
  };

  const handleAddWidget = (newWidgets: CustomWidget[]) => {
    setWidgets((prevWidgets) => {
      const mergedWidgets = keepDefaultWidgets
        ? [
            ...keepWidgets!,
            ...newWidgets.filter(
              (newWidget) => !prevWidgets.some((widget) => widget.id === newWidget.id),
            ),
          ]
        : [
            ...prevWidgets,
            ...newWidgets.filter(
              (newWidget) => !prevWidgets.some((widget) => widget.id === newWidget.id),
            ),
          ];

      const newLayouts: any = DEFAULT_CLEAR_LAYOUT;

      mergedWidgets.forEach((widget, index) => {
        const widgetForLayout12Columns = {
          i: widget.id,
          x: 6 * (index % 2),
          y: 4 * Math.floor(index / 2),
          w: 6,
          h: 4,
        };

        const widgetForLayoutOneColumn = {
          i: widget.id,
          x: 0,
          y: 6 * index,
          w: 12,
          h: 4,
        };

        newLayouts[KEYS.LG].push(widgetForLayout12Columns);
        newLayouts[KEYS.MD].push(widgetForLayout12Columns);
        newLayouts[KEYS.SM].push(widgetForLayout12Columns);
        newLayouts[KEYS.XS].push(widgetForLayoutOneColumn);
        newLayouts[KEYS.XXS].push(widgetForLayoutOneColumn);
      });

      setLayouts(newLayouts);
      return mergedWidgets;
    });
  };

  const handleSettingsWidget = (widget: string) => {
    const randomId = Math.floor(Math.random() * 10000);

    setWidgets((prevWidgets) => {
      const widgetToDuplicate = prevWidgets.find((w) => w.id === widget);
      if (!widgetToDuplicate) return prevWidgets;

      const duplicatedWidget = {
        ...widgetToDuplicate,
        id: `${widgetToDuplicate.id}_${randomId}`,
      };

      return [...prevWidgets, duplicatedWidget];
    });

    setLayouts((prevLayouts: any) => {
      const newLayouts: any = { ...prevLayouts };

      Object.keys(newLayouts).forEach((key) => {
        const widgetLayout = newLayouts[key].find((layout: any) => layout.i === widget);
        if (widgetLayout) {
          const duplicatedLayout = {
            ...widgetLayout,
            i: `${widgetLayout.i}_${randomId}`,
            x: 6 * (newLayouts[key].length % 2),
            y: 4 * Math.floor(newLayouts[key].length / 2),
          };

          newLayouts[key] = [...newLayouts[key], duplicatedLayout];
        }
      });

      return newLayouts;
    });
  };

  const handleDeleteWidget = (widget: string) => {
    setCurrentWidget(widget);
    setIsDeleteWidget(true);
    setOpenCreateWidgetModal(true);
  };

  const handleUnsubscribeWidget = (widget: string) => {
    unsubscribe(widget);
  };

  const deleteWidget = (id: string) => {
    setCurrentWidget('');
    setIsDeleteWidget(false);

    setWidgets((prev) => {
      return prev.filter((w) => w.id !== id);
    });

    handleUnsubscribeWidget(id);

    setLayouts((prev: any) => {
      const filterWidgets = (arr: any) => {
        return arr.filter((w: any) => w.i !== id);
      };

      return {
        [KEYS.LG]: filterWidgets(prev[KEYS.LG]),
        [KEYS.MD]: filterWidgets(prev[KEYS.MD]),
        [KEYS.SM]: filterWidgets(prev[KEYS.SM]),
        [KEYS.XS]: filterWidgets(prev[KEYS.XS]),
        [KEYS.XXS]: filterWidgets(prev[KEYS.XXS]),
      };
    });
  };

  useEffect(() => {
    const saved = getStorage(`WIDGETS_${key}`);

    if (saved && saved.layouts.length) {
      setLayouts(saved.layouts);
      setWidgets(saved.widgets);
    }
  }, [setWidgets, setLayouts, key]);

  useEffect(() => {
    setStorage(`WIDGETS_${key}`, {
      layouts: layouts,
      widgets: widgets,
    });
  }, [widgets, layouts, key]);

  useEffect(() => {
    setWidgets((prevWidgets: any) => {
      return prevWidgets.map((widget: any) => {
        if (widget.isDisabledSettins && widget.isDisabledRemove) {
          return {
            id: WidgetKeys.VolatilitySurfaceMarking,
            key: WidgetKeys.VolatilitySurfaceMarking,
            title: 'Volatility Surface Marking',
          };
        }
        return widget;
      });
    });
  }, [setWidgets]);

  useEffect(() => {
    let isUpdated = false;

    setLayouts((prev: any) => {
      const updateKeys = (arr: any) => {
        if (!arr.some((w: any) => w.i === 'surface-marking')) {
          return arr;
        }

        isUpdated = true;

        return arr.map((w: any) => {
          if (w.i === 'surface-marking') {
            return {
              ...w,
              i: WidgetKeys.VolatilitySurfaceMarking,
              id: WidgetKeys.VolatilitySurfaceMarking,
            };
          }
          return w;
        });
      };

      return {
        [KEYS.LG]: updateKeys(prev[KEYS.LG]),
        [KEYS.MD]: updateKeys(prev[KEYS.MD]),
        [KEYS.SM]: updateKeys(prev[KEYS.SM]),
        [KEYS.XS]: updateKeys(prev[KEYS.XS]),
        [KEYS.XXS]: updateKeys(prev[KEYS.XXS]),
      };
    });

    if (isUpdated) {
      handleResetLayout();
    }
  }, [setLayouts]);

  const renderModal = () => {
    return (
      <HandleWidgetModal
        open={openCreateWidgetModal}
        handleClose={handleCloseCreateWidgetModal}
        handleAddWidget={handleAddWidget}
        isDeleteWidget={isDeleteWidget}
        handleDeleteWidget={deleteWidget}
        currentWidget={currentWidget}
        currentWidgets={widgets}
      />
    );
  };

  const renderWidgets = () => {
    const widgetClassName = (key: string, type: string) => {
      if (!maximized) return `${type}`;

      return maximized === key ? `maximized ${type}` : 'hidden';
    };

    const handleLayoutChange = (
      layout: ReactGridLayout.Layout[],
      layouts: ReactGridLayout.Layouts,
    ) => {
      setLayouts(layouts);
    };

    return widgets && widgets.length > 0 ? (
      <ResponsiveGridLayout
        className="grid"
        layouts={layouts}
        autoSize={true}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 12, sm: 12, xs: 1, xxs: 1 }}
        resizeHandles={['se']}
        draggableHandle=".drag-area"
        onLayoutChange={handleLayoutChange}
        useCSSTransforms={true}
      >
        {widgets.map((widget) => (
          <div key={widget.id} className={widgetClassName(widget.key, widget.key)}>
            <RenderWidget
              widget={widget}
              handleSettingsWidget={handleSettingsWidget}
              handleDeleteWidget={handleDeleteWidget}
              maximizeCard={maximizeCard}
              maximized={maximized}
              isCustomTab={isCustomTab || false}
              providerKey={key}
              showFooter={true}
            />
          </div>
        ))}
      </ResponsiveGridLayout>
    ) : null;
  };

  return {
    renderWidgets,
    widgets,
    setWidgets,
    handleOpenModal,
    handleResetLayout,
    renderModal,
  };
};

export default useWidgets;
